import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  ActivitiesContextProvider,
  useActivitiesContext,
} from 'pages/Activities/activities.context';
import { UsersContextProvider } from 'context/users.context';
import { StoresContextProvider } from 'pages/Stores/stores.context';
import { CampaignsContextProvider } from 'pages/Campaigns/campaigns.context';
import Loading from 'components/Messages/Loading/Loading';
import useQueryActivityAssignment from 'hooks/activities/useQueryActivityAssignment';
import Error from 'components/Messages/Error/Error';
import { useDashboardContext } from 'context/dashboard.context';
import { useAuthContext } from 'context/auth.context';
import { OverviewDetailInterface } from 'components/Overview/overview.types';
import { Tag } from 'components/Tag/Tag';
import { formatDatePickerDate, formatMinutes, getUsersName } from 'utils/formatting';
import { Grid, Typography } from '@mui/material';
import CustomIcon from 'components/Icon/CustomIcon';
import CheckIcon from '@mui/icons-material/Check';
import { themeOptions } from 'theme';
import { Div } from 'components/Elements/Div/Div';
import { hasPermissions } from 'config/ability';
import SectionHeader from 'components/SectionHeader/SectionHeader';
import ContentWrapper from 'components/StyledComponents/ContentWrapper';
import styled from 'styled-components';
import AssignmentSteps from 'pages/Activities/ActivityAssignment/AssignmentSteps';
import { StepWithResponseData } from 'hooks/activities/useQueryActivityAssignments';
import OverviewDetail from 'components/Overview/OverviewDetail/OverviewDetail';
import RowDetails from 'components/Overview/RowDetails/RowDetails';
import CompleteActivityAssignment from 'pages/Activities/ActivityAssignment/CompleteActivityAssignment';

const CompleteActivityWrapper = styled.div`
  margin-top: 1rem;
  width: 100%;
`;

const ActivityAssignmentComponent = () => {
  const { activityId, assignmentId } = useParams();
  const parsedAssignmentId = useMemo(
    () => (assignmentId ? parseInt(assignmentId, 10) : undefined),
    [assignmentId]
  );

  const { getActivityById } = useActivitiesContext();
  const activityById = getActivityById(Number(activityId));
  const { data, loading, error } = useQueryActivityAssignment(parsedAssignmentId);
  const { md } = useDashboardContext();
  const { ability, userTenantId } = useAuthContext();
  const { hash } = useLocation();
  const scrolledToHashRef = useRef(false);

  // If url contains hash like "#step-{stepId}" then we will smooth scroll down to that step after data is loaded.
  useEffect(() => {
    if (
      data?.activityAssignment &&
      hash &&
      hash.startsWith('#step-') &&
      !scrolledToHashRef.current
    ) {
      const element = document.getElementById(hash.slice(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        scrolledToHashRef.current = true;
      }
    }
  }, [data?.activityAssignment, hash]);

  if (!parsedAssignmentId) {
    return <Error message='Activity assignment not found' />;
  }

  if (loading) {
    return <Loading />;
  }

  const assignment = data?.activityAssignment;
  const activity = data?.activityAssignment?.activity;

  if (!assignment || !activity) {
    return <Error message='Activity assignment not found or failed to load' />;
  }

  if (error) {
    return <Error message={error.message} />;
  }

  const ccRecipients =
    activity.ccRecipients?.map((user: { firstName: string; lastName: string }) =>
      getUsersName(user)
    ) || [];

  const stepsWithResponses: StepWithResponseData[] | undefined | null = assignment.steps;

  const details: OverviewDetailInterface[] = [
    { title: 'Description', value: activity.description, richText: true },
    {
      title: 'Tags',
      value: activityById?.tags?.map((tag) => (
        <Tag key={tag.id} color={tag.displayColour} text={tag.name} margin='0 0.75rem 0 0' />
      )),
      hide: !activityById?.tags?.length,
    },
  ];

  const rowDetails: OverviewDetailInterface[] = [
    {
      title: 'Start Date',
      value: formatDatePickerDate(
        (activity.startDate as Date) || new Date().toISOString().slice(0, 10)
      ),
      width: '6rem',
      marginRight: '3.6rem',
    },
    {
      title: 'Estimated Time to Complete',
      value: formatMinutes(activity.estimatedTimeToComplete),
      minWidth: '17rem',
    },
    {
      title: 'Due Date',
      value: formatDatePickerDate(activity.dueDate),
      width: '6rem',
      marginRight: '3.6rem',
    },
    {
      title: 'Actual Time to Complete',
      value: formatMinutes(assignment.actualTimeToComplete || 0),
      minWidth: '17rem',
    },
    { title: 'Campaign', value: assignment.campaign?.name, width: '6rem', marginRight: '3.6rem' },
    { title: 'Store', value: assignment.store?.name, minWidth: '17rem' },
  ];

  const detailsPart2: OverviewDetailInterface[] = [
    {
      title: 'Assigned to',
      value: (assignment.assignedUser ? [getUsersName(assignment.assignedUser)] : [])?.join(', '),
    },
    { title: 'Cc Recipients', value: ccRecipients?.join(', ') },
  ];

  const completeActivity = (
    <CompleteActivityWrapper>
      {assignment?.completedDate ? (
        <Grid item pt={5}>
          <CustomIcon
            label={`Completed on ${formatDatePickerDate(assignment.completedDate)}`}
            icon={<CheckIcon />}
            color={themeOptions.colors.success900}
          />
        </Grid>
      ) : (
        <CompleteActivityAssignment activityAssignment={assignment} />
      )}
    </CompleteActivityWrapper>
  );

  const detailsSection = (
    <Grid item xs={12} md={5} padding='1rem'>
      <Grid container>
        <Grid item xs={12}>
          <Typography variant='h3' fontWeight={500} mb='2.375rem'>
            Activity Details
          </Typography>
        </Grid>
        {details.map((detail) => (
          <OverviewDetail key={`${(typeof detail.value).toString()}-${detail.title}`} {...detail} />
        ))}
        <Div display='flex' width='100%' flexWrap='wrap'>
          <RowDetails rowDetails={rowDetails} />
        </Div>
        {detailsPart2.map((detail) => (
          <OverviewDetail key={`${(typeof detail.value).toString()}-${detail.title}`} {...detail} />
        ))}
        <Div width='100%'>{!!activityById?.id && completeActivity}</Div>
      </Grid>
    </Grid>
  );

  const filteredSteps: StepWithResponseData[] =
    stepsWithResponses && stepsWithResponses.length > 0
      ? stepsWithResponses.filter((curStep) => {
          return (
            // TODO - Currently Users are assigned an entire Activity not individual Steps, this will eventually change
            // hasPermissions(ability, 'read', 'Step', {
            //   tenantId: userTenantId,
            // }) ||
            // hasPermissions(ability, 'read', 'Step', {
            //   stepId: curStep.step.id,
            // }) ||
            hasPermissions(ability, 'read', 'Step', {
              tenantId: userTenantId,
            }) ||
            hasPermissions(ability, 'read', 'ActivityAssignment', {
              userId: assignment?.assignedUser?.id,
            })
          );
        })
      : [];

  const stepOverviewSection = !!filteredSteps?.length && (
    <Grid item xs={12} md={7}>
      <AssignmentSteps activityAssignment={assignment} steps={filteredSteps} />
    </Grid>
  );

  return (
    <>
      <SectionHeader title={activity.subject} margin='0 0 2.6875rem' pageHeader />
      <ContentWrapper>
        <Grid container>
          {md && stepOverviewSection}
          {detailsSection}
          {!md && stepOverviewSection}
        </Grid>
      </ContentWrapper>
    </>
  );
};

const ActivityAssignment = () => (
  <UsersContextProvider>
    <StoresContextProvider>
      <CampaignsContextProvider>
        <ActivitiesContextProvider>
          <ActivityAssignmentComponent />
        </ActivitiesContextProvider>
      </CampaignsContextProvider>
    </StoresContextProvider>
  </UsersContextProvider>
);

export default ActivityAssignment;
